import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { UserPlus, Mail, Trash2, Shield, ChevronDown } from 'lucide-react';
import { api } from '../../lib/api';
import { useUIStore } from '../../store/ui';

interface Member {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  joinedAt?: string;
  avatarUrl?: string;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
}

const ROLES = ['owner', 'admin', 'manager', 'member'];

export function TeamMembers() {
  const { slug } = useParams<{ slug: string }>();
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('member');
  const { addToast } = useUIStore();

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);

  // Role change dropdown
  const [openRoleDropdown, setOpenRoleDropdown] = useState<string | null>(null);

  useEffect(() => {
    fetchMembers();
  }, [slug]);

  async function fetchMembers() {
    try {
      const res = await api.get(`/api/teams/${slug}/members`);
      if (res.members) {
        setMembers(res.members);
      }
      if (res.invitations) {
        setInvitations(res.invitations);
      }
      if (res.userRole) {
        setUserRole(res.userRole);
      }
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviting(true);

    try {
      await api.post(`/api/teams/${slug}/invite`, {
        email: inviteEmail,
        role: inviteRole,
      });

      addToast('Invitation sent successfully!', 'success');
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      fetchMembers();
    } catch (error) {
      addToast('Failed to send invitation', 'error');
    } finally {
      setInviting(false);
    }
  }

  async function handleRoleChange(memberId: string, newRole: string) {
    try {
      await api.patch(`/api/teams/${slug}/members/${memberId}`, {
        role: newRole,
      });
      setMembers(
        members.map((m) =>
          m.id === memberId ? { ...m, role: newRole } : m
        )
      );
      addToast('Role updated successfully', 'success');
    } catch (error) {
      addToast('Failed to update role', 'error');
    }
    setOpenRoleDropdown(null);
  }

  async function handleRemoveMember(memberId: string, memberName: string) {
    if (!confirm(`Are you sure you want to remove ${memberName} from the team?`)) {
      return;
    }

    try {
      await api.delete(`/api/teams/${slug}/members/${memberId}`);
      setMembers(members.filter((m) => m.id !== memberId));
      addToast('Member removed from team', 'success');
    } catch (error) {
      addToast('Failed to remove member', 'error');
    }
  }

  async function handleCancelInvitation(invitationId: string) {
    try {
      await api.delete(`/api/teams/${slug}/invitations/${invitationId}`);
      setInvitations(invitations.filter((i) => i.id !== invitationId));
      addToast('Invitation cancelled', 'success');
    } catch (error) {
      addToast('Failed to cancel invitation', 'error');
    }
  }

  const canManage = ['owner', 'admin'].includes(userRole);
  const canInvite = ['owner', 'admin', 'manager'].includes(userRole);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-600 mt-1">
            Manage your team members and send invitations.
          </p>
        </div>
        {canInvite && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn btn-primary"
          >
            <UserPlus size={18} className="mr-2" />
            Invite Member
          </button>
        )}
      </div>

      {/* Members Table */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">
            Members ({members.length})
          </h2>
        </div>

        <div className="divide-y divide-gray-200">
          {members.map((member) => (
            <div
              key={member.id}
              className="p-4 flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                {member.avatarUrl ? (
                  <img
                    src={member.avatarUrl}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">
                      {member.firstName.charAt(0)}
                      {member.lastName.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <div className="font-medium text-gray-900">
                    {member.firstName} {member.lastName}
                  </div>
                  <div className="text-sm text-gray-500">{member.email}</div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {/* Role Badge/Dropdown */}
                {canManage && member.role !== 'owner' ? (
                  <div className="relative">
                    <button
                      onClick={() =>
                        setOpenRoleDropdown(
                          openRoleDropdown === member.id ? null : member.id
                        )
                      }
                      className="flex items-center space-x-1 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
                    >
                      <Shield size={14} className="text-gray-400" />
                      <span className="capitalize">{member.role}</span>
                      <ChevronDown size={14} className="text-gray-400" />
                    </button>

                    {openRoleDropdown === member.id && (
                      <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                        {ROLES.filter((r) => r !== 'owner').map((role) => (
                          <button
                            key={role}
                            onClick={() => handleRoleChange(member.id, role)}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 capitalize ${
                              member.role === role
                                ? 'text-red-600 font-medium'
                                : 'text-gray-700'
                            }`}
                          >
                            {role}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <span
                    className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                      member.role === 'owner'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {member.role}
                  </span>
                )}

                {/* Remove Button */}
                {canManage && member.role !== 'owner' && (
                  <button
                    onClick={() =>
                      handleRemoveMember(
                        member.id,
                        `${member.firstName} ${member.lastName}`
                      )
                    }
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">
              Pending Invitations ({invitations.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="p-4 flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Mail className="text-yellow-600" size={18} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {invitation.email}
                    </div>
                    <div className="text-sm text-gray-500 capitalize">
                      Invited as {invitation.role}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500">
                    Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                  </span>
                  {canManage && (
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Invite Team Member
              </h2>
            </div>

            <form onSubmit={handleInvite} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="input w-full"
                  placeholder="colleague@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="input w-full"
                >
                  <option value="member">Member</option>
                  <option value="manager">Manager</option>
                  {userRole === 'owner' && <option value="admin">Admin</option>}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  {inviteRole === 'admin'
                    ? 'Admins can manage members and team settings.'
                    : inviteRole === 'manager'
                    ? 'Managers can create bookings and invite members.'
                    : 'Members can view team information and bookings.'}
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="btn btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="btn btn-primary flex-1"
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
